import React, { useState, useRef } from 'react';
import { node, arrayOf, object, func, string } from 'prop-types';
import config from '../../config';
import { FieldDateRangeController, OutsideClickHandler } from '..';
import Tooltip from '../Tooltip/Tooltip';
import ChevronButton from '../ChevronButton/ChevronButton';
import { propTypes } from '../../util/types';
import css from './FieldDates.module.css';
import { useScrollTooltipIntoView } from '../../hooks';

export const dateFieldAccessibleName = text => {
  if (typeof text === 'string' && text.trim()) {
    return `Select dates: ${text.trim()}`;
  }

  return 'Select dates';
};

const FieldDates = ({
  label,
  text,
  unitType,
  timeSlots,
  onChange,
  onReset,
  fieldDateProps,
  isPackage,
  placement,
  onTooltipOpenChange,
}) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const triggerButtonRef = useRef(null);

  const handleTooltipVisibility = isVisible => {
    setIsTooltipVisible(isVisible);

    // Notify parent component about tooltip state change
    if (onTooltipOpenChange) {
      onTooltipOpenChange(isVisible);
    }
  };

  const handleOutsideClick = event => {
    // Check if the click was on the trigger button or its children
    if (triggerButtonRef.current && triggerButtonRef.current.contains(event.target)) {
      return;
    }

    // Only close if the tooltip is visible
    if (isTooltipVisible) {
      handleTooltipVisibility(false);
    }
  };

  const handleButtonClick = () => {
    handleTooltipVisibility(!isTooltipVisible);
  };

  // Scroll tooltip into view when it becomes visible
  useScrollTooltipIntoView(isTooltipVisible, `.${css.dateTooltip}`);

  return (
    <>
      <h5 className={css.formFieldHeading}>{label}</h5>

      {isTooltipVisible && <div className={css.tooltipOverlay} />}

      <Tooltip
        rootClassName={css.dateTooltip}
        onTooltipShown={isVisible => {
          handleTooltipVisibility(isVisible);
        }}
        tooltipShown={isTooltipVisible}
        hideArrow
        hideCloseButtonDesktop
        placement={placement || (isPackage ? 'bottom-end' : 'bottom-end')}
        trigger="none"
        tooltip={
          <OutsideClickHandler
            rootClassName={css.outsideHandler}
            onOutsideClick={handleOutsideClick}
          >
            <FieldDateRangeController
              name="dates"
              unitType={unitType}
              format={null}
              timeSlots={timeSlots}
              onChange={values => {
                if (onChange) onChange(values);

                handleTooltipVisibility(false);
              }}
              onReset={onReset}
              {...fieldDateProps}
            />
          </OutsideClickHandler>
        }
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: () => {
                return [0, 20];
              },
            },
          },
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['bottom-end'],
            },
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              padding: 20,
            },
          },
        ]}
      >
        <ChevronButton
          ref={triggerButtonRef}
          testId="date-chooser"
          onClick={handleButtonClick}
          isExpanded={isTooltipVisible}
          ariaLabel={dateFieldAccessibleName(text)}
        >
          <div className={css.iconContainer}>
            <span>{text}</span>
          </div>
        </ChevronButton>
      </Tooltip>
    </>
  );
};

FieldDates.defaultProps = {
  unitType: config.bookingUnitType,
  timeSlots: null,
  placement: '',
  onTooltipOpenChange: null,
};

FieldDates.propTypes = {
  unitType: propTypes.bookingUnitType,
  label: node.isRequired,
  text: node.isRequired,
  timeSlots: arrayOf(propTypes.timeSlot),
  onChange: func.isRequired,
  fieldDateProps: object.isRequired,
  placement: string,
  onTooltipOpenChange: func,
};

export default FieldDates;
